import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Menu,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Block as BlockIcon,
  LocalOffer as TagIcon,
  Language as GlobeIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend, LineChart, Line
} from 'recharts';

// ─── Theme color ───
const ACCENT = '#6D53F4';
const ACCENT_HOVER = '#5A3FE0';
const ACCENT_LIGHT = '#F0EDFE';

// ─── Shared table header style ───
const thStyle = {
  fontWeight: 600,
  color: '#6B7280',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid #E5E7EB',
  bgcolor: '#FAFAFA',
  py: 1.5,
};

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi',
];

// ─── Sub-tab definitions ───
const SUB_TABS = ['Plans', 'Features', 'Coupons', 'Analytics', 'Settings'];
const PERIOD_OPTIONS = ['7 Days', 'Monthly', 'Yearly'];

// ─── Status color helper ───
const getCouponStatusStyle = (status) => {
  switch (status) {
    case 'Active':
      return { color: '#16A34A', dotColor: '#16A34A', bg: '#DCFCE7' };
    case 'Expired':
      return { color: '#DC2626', dotColor: '#DC2626', bg: '#FEE2E2' };
    case 'Scheduled':
      return { color: '#D97706', dotColor: '#F59E0B', bg: '#FEF3C7' };
    default:
      return { color: '#6B7280', dotColor: '#6B7280', bg: '#F3F4F6' };
  }
};

// ─── Helper: render feature cell value ───
const FeatureValue = ({ value, type }) => {
  if (type === 'boolean') {
    if (value === true) {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
          }}
        >
          <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
        </Box>
      );
    }
    return (
      <CloseIcon sx={{ color: '#D1D5DB', fontSize: 20, display: 'block', mx: 'auto' }} />
    );
  }
  // limit type — render a chip-like label
  if (typeof value === 'string') {
    const isUnlimited = value === 'Unlimited';
    return (
      <Chip
        label={value}
        size="small"
        sx={{
          bgcolor: isUnlimited ? '#EDE9FE' : '#F0FDF4',
          color: isUnlimited ? ACCENT : '#16A34A',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26,
          borderRadius: '6px',
          border: 'none',
        }}
      />
    );
  }
  return <CloseIcon sx={{ color: '#D1D5DB', fontSize: 20, display: 'block', mx: 'auto' }} />;
};

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('Plans');
  const [activePeriod, setActivePeriod] = useState('Monthly');
  const [searchQuery, setSearchQuery] = useState('');
  const [couponSearchQuery, setCouponSearchQuery] = useState('');
  const [selectedFeatureCategory, setSelectedFeatureCategory] = useState('All');

  // ─── Data State ───
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  // ─── Modal States ───
  const [isCreatePlanOpen, setIsCreatePlanOpen] = useState(false);
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [isAddFeatureOpen, setIsAddFeatureOpen] = useState(false);
  const [isEditFeatureOpen, setIsEditFeatureOpen] = useState(false);
  const [isCreateCouponOpen, setIsCreateCouponOpen] = useState(false);
  const [isEditCouponOpen, setIsEditCouponOpen] = useState(false);
  const [planMenuAnchorEl, setPlanMenuAnchorEl] = useState(null);
  const [selectedPlanForMenu, setSelectedPlanForMenu] = useState(null);
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState(null);
  const [selectedFeatureForEdit, setSelectedFeatureForEdit] = useState(null);
  const [selectedCouponForEdit, setSelectedCouponForEdit] = useState(null);

  // ─── Snackbar State ───
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ─── Create Plan Form State ───
  const [planForm, setPlanForm] = useState({
    name: '', description: '', amount: '', period: 'monthly',
    interval: 1, trialPeriod: 0, enableTrialPeriod: false, isActive: true, displayOrder: 0, subscriberLimit: '',
  });
  const [planSubmitting, setPlanSubmitting] = useState(false);

  // ─── Create Feature Form State ───
  const [featureForm, setFeatureForm] = useState({
    name: '', category: 'Discovery', subtitle: '', status: true, planLimits: [],
  });
  const [featureSubmitting, setFeatureSubmitting] = useState(false);

  // ─── Coupon Form State ───
  const [couponForm, setCouponForm] = useState({
    code: '', description: '', discountValue: '', discountType: 'Percentage',
    applicablePlan: 'All', scopeType: 'All India', states: [],
    usagePerUser: 1, newUsersOnly: false, usageLimit: '', expiryDate: '', status: 'Active',
  });
  const [couponSubmitting, setCouponSubmitting] = useState(false);

  // ─── Coupon filter state ───
  const [couponScopeFilter, setCouponScopeFilter] = useState('All');
  const [couponStatusFilter, setCouponStatusFilter] = useState('All');

  // ─── Settings Form State ───
  const [settingsForm, setSettingsForm] = useState({
    defaultTrialPeriod: 7,
    gracePeriod: 3,
    defaultCurrency: 'INR',
    billingCycle: 'From purchase date',
    autoRenewal: true,
    allowPause: true,
    downgradePolicy: 'End of billing cycle',
    prorationMode: 'Credit unused time',
    paymentReceipts: true,
    renewalReminder: true,
    reminderTiming: 3,
    expiryAlert: true,
    enableWebhooks: false,
  });
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);

  // ─── Analytics State ───
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // ─── Snackbar helper ───
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── DATA FETCHING ───
  // ═══════════════════════════════════════════════════════════════════════════

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/razorpay/plans');
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
      showSnackbar(err.response?.data?.message || 'Failed to fetch plans', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeatures = useCallback(async () => {
    try {
      const res = await axios.get('/admin/razorpay/features');
      setFeatures(res.data.features || []);
    } catch (err) {
      console.error('Error fetching features:', err);
      showSnackbar(err.response?.data?.message || 'Failed to fetch features', 'error');
    }
  }, []);

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await axios.get('/admin/razorpay/coupons');
      setCoupons(res.data.coupons || []);
    } catch (err) {
      console.error('Error fetching coupons:', err);
      showSnackbar(err.response?.data?.message || 'Failed to fetch coupons', 'error');
    }
  }, []);

  const fetchSubscriptionSettings = useCallback(async () => {
    try {
      const res = await axios.get('/admin/razorpay/subscription-settings');
      if (res.data.settings) {
        setSettingsForm(res.data.settings);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      showSnackbar(err.response?.data?.message || 'Failed to fetch settings', 'error');
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchFeatures();
    fetchCoupons();
    fetchSubscriptionSettings();
  }, [fetchPlans, fetchFeatures, fetchCoupons, fetchSubscriptionSettings]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const res = await axios.get('/admin/razorpay/analytics');
      if (res.data.data) {
        setAnalyticsData(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      showSnackbar(err.response?.data?.message || 'Failed to fetch analytics', 'error');
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'Analytics') {
      fetchAnalytics();
    }
  }, [activeTab, fetchAnalytics]);

  const handleUpdateSettings = async () => {
    try {
      setSettingsSubmitting(true);
      const res = await axios.put('/admin/razorpay/subscription-settings', settingsForm);
      if (res.data.settings) {
        setSettingsForm(res.data.settings);
        showSnackbar('Settings updated successfully!');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      showSnackbar(err.response?.data?.message || 'Failed to update settings', 'error');
    } finally {
      setSettingsSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── PLAN CRUD ───
  // ═══════════════════════════════════════════════════════════════════════════

  const handleCreatePlan = async () => {
    if (!planForm.name || !planForm.amount) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    try {
      setPlanSubmitting(true);
      await axios.post('/admin/razorpay/plans', {
        name: planForm.name,
        description: planForm.description,
        amount: Math.round(Number(planForm.amount) * 100), // Convert rupees to paise
        period: planForm.period,
        interval: planForm.interval,
        trialPeriod: planForm.enableTrialPeriod ? planForm.trialPeriod : 0,
        displayOrder: planForm.displayOrder,
        isActive: planForm.isActive,
        subscriberLimit: planForm.subscriberLimit ? Number(planForm.subscriberLimit) : null,
      });
      showSnackbar('Plan created successfully!');
      setIsCreatePlanOpen(false);
      setPlanForm({ name: '', description: '', amount: '', period: 'monthly', interval: 1, trialPeriod: 0, enableTrialPeriod: false, isActive: true, displayOrder: 0, subscriberLimit: '' });
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create plan', 'error');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const openEditPlan = (plan) => {
    setSelectedPlanForEdit(plan);
    const hasTrial = plan.trialPeriod && plan.trialPeriod > 0;
    setPlanForm({
      name: plan.name || '',
      description: plan.description || '',
      amount: plan.amount ? (plan.amount / 100).toString() : '',
      period: plan.period || 'monthly',
      interval: plan.interval || 1,
      trialPeriod: plan.trialPeriod || 0,
      enableTrialPeriod: hasTrial,
      isActive: plan.isActive !== false,
      displayOrder: plan.displayOrder || 0,
      subscriberLimit: plan.subscriberLimit ? plan.subscriberLimit.toString() : '',
    });
    setIsEditPlanOpen(true);
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlanForEdit) return;
    if (!planForm.name || !planForm.amount) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    try {
      setPlanSubmitting(true);
      await axios.put(`/admin/razorpay/plans/${selectedPlanForEdit._id}`, {
        name: planForm.name,
        description: planForm.description,
        amount: Math.round(Number(planForm.amount) * 100),
        period: planForm.period,
        interval: planForm.interval,
        trialPeriod: planForm.enableTrialPeriod ? planForm.trialPeriod : 0,
        displayOrder: planForm.displayOrder,
        isActive: planForm.isActive,
        subscriberLimit: planForm.subscriberLimit ? Number(planForm.subscriberLimit) : null,
      });
      showSnackbar('Plan updated successfully!');
      setIsEditPlanOpen(false);
      setSelectedPlanForEdit(null);
      setPlanForm({ name: '', description: '', amount: '', period: 'monthly', interval: 1, trialPeriod: 0, enableTrialPeriod: false, isActive: true, displayOrder: 0, subscriberLimit: '' });
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update plan', 'error');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleDuplicatePlan = async (plan) => {
    try {
      setPlanSubmitting(true);
      await axios.post('/admin/razorpay/plans', {
        name: `${plan.name} (Copy)`,
        description: plan.description,
        amount: plan.amount,
        period: plan.period,
        interval: plan.interval,
        trialPeriod: plan.trialPeriod || 0,
        displayOrder: (plan.displayOrder || 0) + 1,
        subscriberLimit: plan.subscriberLimit || null,
      });
      showSnackbar('Plan duplicated successfully!');
      setPlanMenuAnchorEl(null);
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to duplicate plan', 'error');
    } finally {
      setPlanSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await axios.delete(`/admin/razorpay/plans/${planId}`);
      showSnackbar('Plan deleted successfully!');
      setPlanMenuAnchorEl(null);
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete plan', 'error');
    }
  };

  const handleDeactivatePlan = async (plan) => {
    try {
      const newStatus = !plan.isActive;
      await axios.put(`/admin/razorpay/plans/${plan._id}`, { isActive: newStatus });
      showSnackbar(`Plan ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      setPlanMenuAnchorEl(null);
      fetchPlans();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update plan status', 'error');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── FEATURE CRUD ───
  // ═══════════════════════════════════════════════════════════════════════════

  const handleCreateFeature = async () => {
    if (!featureForm.name || !featureForm.category) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    try {
      setFeatureSubmitting(true);
      // Build planLimits from the form
      const planLimits = plans.map((plan) => {
        const limitKey = `plan_${plan._id}`;
        const typeKey = `planType_${plan._id}`;
        const valueKey = `planValue_${plan._id}`;
        const type = featureForm[typeKey] || 'boolean';
        let value;
        if (type === 'boolean') {
          value = featureForm[limitKey] === 'Available';
        } else {
          value = featureForm[valueKey] || '';
        }
        return { plan: plan._id, type, value };
      });

      await axios.post('/admin/razorpay/features', {
        name: featureForm.name,
        category: featureForm.category,
        subtitle: featureForm.subtitle,
        status: featureForm.status,
        planLimits,
      });
      showSnackbar('Feature created successfully!');
      setIsAddFeatureOpen(false);
      setFeatureForm({ name: '', category: 'Discovery', subtitle: '', status: true, planLimits: [] });
      fetchFeatures();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create feature', 'error');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const handleUpdateFeature = async () => {
    if (!selectedFeatureForEdit) return;
    try {
      setFeatureSubmitting(true);
      const planLimits = plans.map((plan) => {
        const limitKey = `plan_${plan._id}`;
        const typeKey = `planType_${plan._id}`;
        const valueKey = `planValue_${plan._id}`;
        const type = featureForm[typeKey] || 'boolean';
        let value;
        if (type === 'boolean') {
          value = featureForm[limitKey] === 'Available';
        } else {
          value = featureForm[valueKey] || '';
        }
        return { plan: plan._id, type, value };
      });

      await axios.put(`/admin/razorpay/features/${selectedFeatureForEdit._id}`, {
        name: featureForm.name,
        category: featureForm.category,
        subtitle: featureForm.subtitle,
        status: featureForm.status,
        planLimits,
      });
      showSnackbar('Feature updated successfully!');
      setIsEditFeatureOpen(false);
      setSelectedFeatureForEdit(null);
      fetchFeatures();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update feature', 'error');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    try {
      await axios.delete(`/admin/razorpay/features/${featureId}`);
      showSnackbar('Feature deleted successfully!');
      fetchFeatures();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete feature', 'error');
    }
  };

  const handleToggleFeatureStatus = async (feature) => {
    try {
      await axios.put(`/admin/razorpay/features/${feature._id}`, {
        status: !feature.status,
      });
      fetchFeatures();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to toggle feature', 'error');
    }
  };

  const openEditFeature = (feature) => {
    setSelectedFeatureForEdit(feature);
    const formData = {
      name: feature.name,
      category: feature.category,
      subtitle: feature.subtitle || '',
      status: feature.status,
    };
    // Populate plan limit fields
    (feature.planLimits || []).forEach((pl) => {
      const planId = pl.plan?._id || pl.plan;
      formData[`planType_${planId}`] = pl.type;
      if (pl.type === 'boolean') {
        formData[`plan_${planId}`] = pl.value ? 'Available' : 'Not Available';
      } else {
        formData[`plan_${planId}`] = 'Limit';
        formData[`planValue_${planId}`] = pl.value || '';
      }
    });
    setFeatureForm(formData);
    setIsEditFeatureOpen(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── COUPON CRUD ───
  // ═══════════════════════════════════════════════════════════════════════════

  const handleCreateCoupon = async () => {
    if (!couponForm.code || !couponForm.description || !couponForm.discountValue || !couponForm.usageLimit || !couponForm.expiryDate) {
      showSnackbar('Please fill all required fields', 'error');
      return;
    }
    try {
      setCouponSubmitting(true);
      await axios.post('/admin/razorpay/coupons', couponForm);
      showSnackbar('Coupon created successfully!');
      setIsCreateCouponOpen(false);
      setCouponForm({
        code: '', description: '', discountValue: '', discountType: 'Percentage',
        applicablePlan: 'All', scopeType: 'All India', states: [],
        usagePerUser: 1, newUsersOnly: false, usageLimit: '', expiryDate: '', status: 'Active',
      });
      fetchCoupons();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create coupon', 'error');
    } finally {
      setCouponSubmitting(false);
    }
  };

  const handleUpdateCoupon = async () => {
    if (!selectedCouponForEdit) return;
    try {
      setCouponSubmitting(true);
      await axios.put(`/admin/razorpay/coupons/${selectedCouponForEdit._id}`, couponForm);
      showSnackbar('Coupon updated successfully!');
      setIsEditCouponOpen(false);
      setSelectedCouponForEdit(null);
      fetchCoupons();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update coupon', 'error');
    } finally {
      setCouponSubmitting(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await axios.delete(`/admin/razorpay/coupons/${couponId}`);
      showSnackbar('Coupon deleted successfully!');
      fetchCoupons();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete coupon', 'error');
    }
  };

  const openEditCoupon = (coupon) => {
    setSelectedCouponForEdit(coupon);
    setCouponForm({
      code: coupon.code,
      description: coupon.description,
      discountValue: coupon.discountValue,
      discountType: coupon.discountType,
      applicablePlan: coupon.applicablePlan,
      scopeType: coupon.scopeType,
      states: coupon.states || [],
      usagePerUser: coupon.usagePerUser,
      newUsersOnly: coupon.newUsersOnly,
      usageLimit: coupon.usageLimit,
      expiryDate: coupon.expiryDate ? new Date(coupon.expiryDate).toISOString().split('T')[0] : '',
      status: coupon.status,
    });
    setIsEditCouponOpen(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── COMPUTED DATA ───
  // ═══════════════════════════════════════════════════════════════════════════

  const filteredPlans = plans.map(plan => {
    return {
      ...plan,
      displayPrice: plan.amount / 100,
      displayDuration: plan.period.charAt(0).toUpperCase() + plan.period.slice(1),
    };
  }).filter((plan) => {
    const matchesSearch = plan.name.toLowerCase().includes(searchQuery.toLowerCase());
    const mappedPeriod = plan.period === 'weekly' ? '7 Days' : (plan.period === 'yearly' ? 'Yearly' : 'Monthly');
    const matchesPeriod = mappedPeriod === activePeriod;
    return matchesSearch && matchesPeriod;
  });

  const activeFeatures = features.filter((f) => f.status);

  const featureCategories = ['All', ...new Set(features.map(f => f.category).filter(Boolean))];

  const filteredFeatures = selectedFeatureCategory === 'All'
    ? features
    : features.filter(f => f.category === selectedFeatureCategory);

  // Helper to get feature value for a plan
  const getFeaturePlanValue = (feature, planName) => {
    const pl = (feature.planLimits || []).find(
      (limit) => limit.plan?.name === planName || limit.plan === planName
    );
    if (!pl) return { value: false, type: 'boolean' };
    return { value: pl.value, type: pl.type };
  };

  // Coupon filters
  const filteredCoupons = coupons.filter((c) => {
    const matchesSearch = c.code.toLowerCase().includes(couponSearchQuery.toLowerCase()) || c.description.toLowerCase().includes(couponSearchQuery.toLowerCase());
    const matchesScope = couponScopeFilter === 'All' ? true : couponScopeFilter === 'State-wise' ? c.scopeType === 'State-wise' : c.scopeType === 'All India';
    const matchesStatus = couponStatusFilter === 'All' ? true : c.status === couponStatusFilter;
    return matchesSearch && matchesScope && matchesStatus;
  });

  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter((c) => c.status === 'Active').length;
  const expiredCoupons = coupons.filter((c) => c.status === 'Expired').length;
  const totalRedemptions = coupons.reduce((sum, c) => sum + (c.usageCount || 0), 0);

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RENDER PLANS TAB ───
  // ═══════════════════════════════════════════════════════════════════════════
  const renderPlansTab = () => (
    <Box>
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', mb: 4, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>Plans Overview</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>View and manage all subscription plans.</Typography>
          </Box>
          <TextField
            placeholder="Search plans..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: ACCENT }, '&.Mui-focused fieldset': { borderColor: ACCENT } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} /></InputAdornment> }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: ACCENT }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={thStyle}>Plan Name</TableCell>
                  <TableCell sx={thStyle}>Price</TableCell>
                  <TableCell sx={thStyle}>Duration</TableCell>
                  <TableCell sx={thStyle}>Trial Period</TableCell>
                  <TableCell sx={thStyle}>Subscribers</TableCell>
                  <TableCell sx={thStyle}>Status</TableCell>
                  <TableCell sx={thStyle}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPlans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>
                      {searchQuery ? 'No plans match your search.' : 'No plans created yet. Click "Create Plan" to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPlans.map((plan) => (
                    <TableRow key={plan._id} sx={{ '&:hover': { bgcolor: '#FAFAFE' }, '& td': { borderBottom: '1px solid #F3F4F6', py: 2, fontSize: '0.875rem' } }}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, color: '#111827' }}>{plan.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, color: '#374151' }}>₹{plan.displayPrice.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#6B7280' }}>{plan.displayDuration}</TableCell>
                      <TableCell sx={{ color: '#6B7280' }}>{plan.trialPeriod ? `${plan.trialPeriod} Days` : '—'}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, color: '#374151' }}>{(plan.subscriberCount || 0).toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: plan.isActive ? '#16A34A' : '#DC2626' }} />
                          <Typography sx={{ color: plan.isActive ? '#16A34A' : '#DC2626', fontWeight: 600, fontSize: '0.8rem' }}>
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={() => openEditPlan(plan)} sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 500, fontSize: '0.8rem', minWidth: 'auto', px: 1.5, '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}>Edit</Button>
                          <IconButton size="small" onClick={(e) => { setPlanMenuAnchorEl(e.currentTarget); setSelectedPlanForMenu(plan); }} sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}>
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Feature Comparison */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #F3F4F6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>Feature Comparison</Typography>
            <Chip label={`${activeFeatures.length}/${features.length} active`} size="small" sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 600, fontSize: '0.7rem', height: 24, borderRadius: '6px' }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>Compare features and limits across all plans.</Typography>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thStyle, width: '22%' }}>Feature</TableCell>
                {plans.filter(p => p.isActive).map(plan => (
                  <TableCell key={plan._id} sx={{ ...thStyle, textAlign: 'center', width: '13%' }}>{plan.name}</TableCell>
                ))}
                <TableCell sx={{ ...thStyle, width: '16%' }}>Status</TableCell>
                <TableCell sx={{ ...thStyle, width: '10%' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature) => (
                <TableRow key={feature._id} sx={{ '&:hover': { bgcolor: '#FAFAFE' }, '& td': { borderBottom: '1px solid #F3F4F6', py: 1.8 } }}>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>{feature.name}</Typography>
                      {feature.subtitle && (
                        <Chip label={feature.subtitle} size="small" sx={{ mt: 0.5, bgcolor: '#F3F4F6', color: '#9CA3AF', fontWeight: 500, fontSize: '0.65rem', height: 20, borderRadius: '4px' }} />
                      )}
                    </Box>
                  </TableCell>
                  {plans.filter(p => p.isActive).map(plan => {
                    const pv = getFeaturePlanValue(feature, plan.name);
                    return (
                      <TableCell key={plan._id} sx={{ textAlign: 'center' }}>
                        <FeatureValue value={pv.value} type={pv.type} />
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch checked={feature.status} onChange={() => handleToggleFeatureStatus(feature)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: feature.status ? '#16A34A' : '#9CA3AF' }}>
                        {feature.status ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => openEditFeature(feature)} sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 500, fontSize: '0.78rem', minWidth: 'auto', px: 1, '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F3F4F6' }}>
          <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
            Toggle features on or off to control visibility across all plans.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RENDER FEATURES TAB ───
  // ═══════════════════════════════════════════════════════════════════════════
  const renderFeaturesTab = () => {
    const totalFeaturesCount = features.length;
    const activeCount = features.filter(f => f.status).length;
    const inactiveCount = totalFeaturesCount - activeCount;
    const categoriesCount = new Set(features.map(f => f.category)).size;

    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[
            { label: 'Total Features', value: totalFeaturesCount, color: ACCENT },
            { label: 'Active Features', value: activeCount, color: '#16A34A' },
            { label: 'Inactive Features', value: inactiveCount, color: '#DC2626' },
            { label: 'Categories', value: categoriesCount, color: '#0284C7' },
          ].map((stat) => (
            <Paper key={stat.label} elevation={0} sx={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5 }}>
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8rem', mb: 0.5, fontWeight: 500 }}>{stat.label}</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '1.75rem', color: stat.color }}>{stat.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>All Features</Typography>
              <Chip label={`${activeCount}/${totalFeaturesCount} active`} size="small" sx={{ bgcolor: '#F0EDFE', color: ACCENT, fontWeight: 600, fontSize: '0.7rem', height: 24, borderRadius: '12px' }} />
            </Box>
            <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => { setFeatureForm({ name: '', category: 'Discovery', subtitle: '', status: true }); setIsAddFeatureOpen(true); }} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 2, py: 1, fontSize: '0.82rem', bgcolor: ACCENT, color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: ACCENT_HOVER } }}>
              Add Feature
            </Button>
          </Box>

          <Box sx={{ px: 3, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {featureCategories.map((cat) => (
              <Chip key={cat} label={cat} onClick={() => setSelectedFeatureCategory(cat)} sx={{ bgcolor: selectedFeatureCategory === cat ? ACCENT : '#F3F4F6', color: selectedFeatureCategory === cat ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.75rem', borderRadius: '16px', border: 'none', cursor: 'pointer', '&:hover': { bgcolor: selectedFeatureCategory === cat ? ACCENT_HOVER : '#E5E7EB' } }} />
            ))}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...thStyle, pl: 4 }}>Feature</TableCell>
                  <TableCell sx={thStyle}>Category</TableCell>
                  {plans.filter(p => p.isActive).map(plan => (
                    <TableCell key={plan._id} sx={{ ...thStyle, textAlign: 'center' }}>{plan.name}</TableCell>
                  ))}
                  <TableCell sx={thStyle}>Status</TableCell>
                  <TableCell sx={thStyle}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFeatures.length === 0 ? (
                  <TableRow><TableCell colSpan={5 + plans.filter(p => p.isActive).length} sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>No features found.</TableCell></TableRow>
                ) : (
                  filteredFeatures.map((feature) => (
                    <TableRow key={feature._id} sx={{ opacity: feature.status ? 1 : 0.6, '&:hover': { bgcolor: '#FAFAFE' }, '& td': { borderBottom: '1px solid #F3F4F6', py: 1.5 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <DragIndicatorIcon sx={{ color: '#D1D5DB', fontSize: 18, cursor: 'grab' }} />
                          <Box>
                            <Typography sx={{ fontWeight: 600, color: feature.status ? '#111827' : '#6B7280', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                              {feature.name}
                              {feature.subtitle && (<Chip label={feature.subtitle} size="small" sx={{ bgcolor: '#F3F4F6', color: '#9CA3AF', fontWeight: 600, fontSize: '0.65rem', height: 20, borderRadius: '4px' }} />)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={feature.category} size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 500, fontSize: '0.7rem', height: 24, borderRadius: '6px' }} />
                      </TableCell>
                      {plans.filter(p => p.isActive).map(plan => {
                        const pv = getFeaturePlanValue(feature, plan.name);
                        return (
                          <TableCell key={plan._id} sx={{ textAlign: 'center' }}>
                            <FeatureValue value={pv.value} type={pv.type} />
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch checked={feature.status} onChange={() => handleToggleFeatureStatus(feature)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: feature.status ? ACCENT : '#9CA3AF' }}>{feature.status ? 'Active' : 'Inactive'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => openEditFeature(feature)} sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 500, fontSize: '0.78rem', minWidth: 'auto', px: 1, border: '1px solid #E5E7EB', borderRadius: '6px', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT, borderColor: ACCENT } }}>Edit</Button>
                          <IconButton size="small" onClick={() => handleDeleteFeature(feature._id)} sx={{ color: '#D1D5DB', border: '1px solid #E5E7EB', borderRadius: '6px', p: '4px', '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2', borderColor: '#DC2626' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── RENDER COUPONS TAB ───
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCouponsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {[
          { label: 'Total Coupons', value: totalCoupons, color: ACCENT },
          { label: 'Active', value: activeCoupons, color: '#16A34A' },
          { label: 'Expired', value: expiredCoupons, color: '#DC2626' },
          { label: 'Total Redemptions', value: totalRedemptions, color: ACCENT },
        ].map((stat) => (
          <Paper key={stat.label} elevation={0} sx={{ flex: 1, border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: stat.color, borderRadius: '12px 0 0 12px' } }}>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.78rem', mb: 0.5 }}>{stat.label}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.75rem', color: '#111827', lineHeight: 1.2 }}>{stat.value.toLocaleString()}</Typography>
          </Paper>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, bgcolor: '#FAFAFA', p: 0.5, borderRadius: '10px', border: '1px solid #F3F4F6' }}>
          {['All', 'All India', 'State-wise'].map((filter) => (
            <Button key={filter} onClick={() => setCouponScopeFilter(filter)} sx={{ textTransform: 'none', fontWeight: couponScopeFilter === filter ? 600 : 500, fontSize: '0.8rem', color: couponScopeFilter === filter ? ACCENT : '#6B7280', bgcolor: couponScopeFilter === filter ? '#fff' : 'transparent', boxShadow: couponScopeFilter === filter ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', px: 2, py: 0.5, borderRadius: '6px', minWidth: 'auto', '&:hover': { bgcolor: couponScopeFilter === filter ? '#fff' : '#F3F4F6' } }}>
              {filter}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, bgcolor: '#FAFAFA', p: 0.5, borderRadius: '10px', border: '1px solid #F3F4F6' }}>
          {['All', 'Active', 'Scheduled', 'Expired'].map((filter) => (
            <Button key={filter} onClick={() => setCouponStatusFilter(filter)} sx={{ textTransform: 'none', fontWeight: couponStatusFilter === filter ? 600 : 500, fontSize: '0.8rem', color: couponStatusFilter === filter ? ACCENT : '#6B7280', bgcolor: couponStatusFilter === filter ? '#fff' : 'transparent', boxShadow: couponStatusFilter === filter ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', px: 2, py: 0.5, borderRadius: '6px', minWidth: 'auto', '&:hover': { bgcolor: couponStatusFilter === filter ? '#fff' : '#F3F4F6' } }}>
              {filter}
            </Button>
          ))}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>Coupon Codes</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>Create and manage discount coupons for subscription plans.</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField placeholder="Search coupons..." size="small" value={couponSearchQuery} onChange={(e) => setCouponSearchQuery(e.target.value)} sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' }, '&:hover fieldset': { borderColor: ACCENT }, '&.Mui-focused fieldset': { borderColor: ACCENT } } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF', fontSize: 20 }} /></InputAdornment> }} />
            <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={() => { setCouponForm({ code: '', description: '', discountValue: '', discountType: 'Percentage', applicablePlan: 'All', scopeType: 'All India', states: [], usagePerUser: 1, newUsersOnly: false, usageLimit: '', expiryDate: '', status: 'Active' }); setIsCreateCouponOpen(true); }} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 2, py: 1, fontSize: '0.82rem', bgcolor: ACCENT, color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: ACCENT_HOVER, boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)' } }}>
              Create Coupon
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Code</TableCell>
                <TableCell sx={thStyle}>Description</TableCell>
                <TableCell sx={thStyle}>Discount</TableCell>
                <TableCell sx={thStyle}>Plan</TableCell>
                <TableCell sx={thStyle}>Scope / States</TableCell>
                <TableCell sx={{ ...thStyle, width: '12%' }}>Usage</TableCell>
                <TableCell sx={thStyle}>Expiry</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCoupons.length === 0 ? (
                <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', py: 6, color: '#9CA3AF' }}>No coupons found.</TableCell></TableRow>
              ) : (
                filteredCoupons.map((coupon) => {
                  const statusStyle = getCouponStatusStyle(coupon.status);
                  const usagePercent = coupon.usageLimit ? (coupon.usageCount / coupon.usageLimit) * 100 : 0;
                  const progressColor = usagePercent >= 100 ? '#DC2626' : usagePercent >= 75 ? '#F59E0B' : ACCENT;

                  return (
                    <TableRow key={coupon._id} sx={{ '&:hover': { bgcolor: '#FAFAFE' }, '& td': { borderBottom: '1px solid #F3F4F6', py: 2, fontSize: '0.875rem' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Chip label={coupon.code} size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em', color: ACCENT, bgcolor: ACCENT_LIGHT, borderRadius: '6px', height: 28 }} />
                          <IconButton size="small" onClick={() => { navigator.clipboard.writeText(coupon.code); showSnackbar('Coupon code copied!'); }} sx={{ p: 0.3, color: '#9CA3AF', '&:hover': { color: ACCENT } }}>
                            <CopyIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#374151', fontSize: '0.84rem', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{coupon.description}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TagIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.84rem' }}>
                              {coupon.discountType === 'Flat' ? `₹${coupon.discountValue}` : `${coupon.discountValue}%`}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', pl: 2.2 }}>{coupon.discountType}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={coupon.applicablePlan} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: '#F3F4F6', color: '#374151', height: 24, borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 180 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {coupon.scopeType === 'All India' ? <GlobeIcon sx={{ fontSize: 14, color: ACCENT }} /> : <LocationIcon sx={{ fontSize: 14, color: ACCENT }} />}
                            <Typography sx={{ fontWeight: 600, color: ACCENT, fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {coupon.scopeType === 'All India' ? 'All India' : (coupon.states || []).join(', ')}
                            </Typography>
                          </Box>
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>{coupon.usagePerUser}× per user</Typography>
                          {coupon.newUsersOnly && <Typography sx={{ color: '#F59E0B', fontSize: '0.75rem', fontWeight: 500 }}>New users only</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: ACCENT }}>{(coupon.usageCount || 0).toLocaleString()}</Typography>
                            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>/ {coupon.usageLimit.toLocaleString()}</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(usagePercent, 100)} sx={{ height: 4, borderRadius: 2, bgcolor: '#E5E7EB', '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 2 } }} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.82rem' }}>
                          {coupon.expiryDate ? new Date(coupon.expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}><Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusStyle.dotColor }} />{coupon.status}</Box>}
                          size="small"
                          sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, fontSize: '0.75rem', height: 24, borderRadius: '6px', border: 'none' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />} onClick={() => openEditCoupon(coupon)} sx={{ textTransform: 'none', color: '#4B5563', fontWeight: 500, fontSize: '0.78rem', minWidth: 'auto', px: 1, border: '1px solid #E5E7EB', borderRadius: '6px', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT, borderColor: ACCENT } }}>Edit</Button>
                          <IconButton size="small" onClick={() => handleDeleteCoupon(coupon._id)} sx={{ color: '#D1D5DB', border: '1px solid #E5E7EB', borderRadius: '6px', p: '4px', '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2', borderColor: '#DC2626' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ─── Settings Tab ───
  const renderSettingsTab = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto', pb: 8 }}>
      {/* General Settings */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>General Settings</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Configure core subscription behaviour for your platform.</Typography>
        </Box>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Default Trial Period</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Days of free trial for new subscribers.</Typography>
            </Box>
            <TextField
              size="small"
              type="number"
              sx={{ width: 150 }}
              value={settingsForm.defaultTrialPeriod}
              onChange={(e) => setSettingsForm({ ...settingsForm, defaultTrialPeriod: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Grace Period</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Days after expiry before access is revoked.</Typography>
            </Box>
            <TextField
              size="small"
              type="number"
              sx={{ width: 150 }}
              value={settingsForm.gracePeriod}
              onChange={(e) => setSettingsForm({ ...settingsForm, gracePeriod: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Default Currency</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Currency shown to all subscribers.</Typography>
            </Box>
            <FormControl size="small" sx={{ width: 200 }}>
              <Select value={settingsForm.defaultCurrency} onChange={(e) => setSettingsForm({ ...settingsForm, defaultCurrency: e.target.value })}>
                <MenuItem value="INR">INR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Billing Cycle</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>When subscription renewal is counted from.</Typography>
            </Box>
            <FormControl size="small" sx={{ width: 200 }}>
              <Select value={settingsForm.billingCycle} onChange={(e) => setSettingsForm({ ...settingsForm, billingCycle: e.target.value })}>
                <MenuItem value="From purchase date">From purchase date</MenuItem>
                <MenuItem value="Start of next month">Start of next month</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Renewal & Cancellation */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Renewal & Cancellation</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Control how subscriptions renew, pause, and downgrade.</Typography>
        </Box>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Auto-Renewal</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Automatically renew subscriptions before expiry.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.autoRenewal} onChange={(e) => setSettingsForm({ ...settingsForm, autoRenewal: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.autoRenewal ? ACCENT : '#9CA3AF' }}>{settingsForm.autoRenewal ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Allow Pause</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Let users pause their subscription temporarily.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.allowPause} onChange={(e) => setSettingsForm({ ...settingsForm, allowPause: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.allowPause ? ACCENT : '#9CA3AF' }}>{settingsForm.allowPause ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Downgrade Policy</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>When plan downgrades take effect.</Typography>
            </Box>
            <FormControl size="small" sx={{ width: 250 }}>
              <Select value={settingsForm.downgradePolicy} onChange={(e) => setSettingsForm({ ...settingsForm, downgradePolicy: e.target.value })}>
                <MenuItem value="Immediate">Immediate</MenuItem>
                <MenuItem value="End of billing cycle">End of billing cycle</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Proration Mode</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>How credits are applied when upgrading mid-cycle.</Typography>
            </Box>
            <FormControl size="small" sx={{ width: 250 }}>
              <Select value={settingsForm.prorationMode} onChange={(e) => setSettingsForm({ ...settingsForm, prorationMode: e.target.value })}>
                <MenuItem value="Credit unused time">Credit unused time</MenuItem>
                <MenuItem value="No proration">No proration</MenuItem>
                <MenuItem value="Always bill full amount">Always bill full amount</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Email Notifications */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Email Notifications</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Configure automated emails sent to subscribers.</Typography>
        </Box>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Payment Receipts</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Send receipt email after every successful payment.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.paymentReceipts} onChange={(e) => setSettingsForm({ ...settingsForm, paymentReceipts: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.paymentReceipts ? ACCENT : '#9CA3AF' }}>{settingsForm.paymentReceipts ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Renewal Reminder</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Send email before subscription renews.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.renewalReminder} onChange={(e) => setSettingsForm({ ...settingsForm, renewalReminder: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.renewalReminder ? ACCENT : '#9CA3AF' }}>{settingsForm.renewalReminder ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Reminder Timing</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>How many days before renewal to send reminder.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                sx={{ width: 100 }}
                value={settingsForm.reminderTiming}
                onChange={(e) => setSettingsForm({ ...settingsForm, reminderTiming: Number(e.target.value) })}
                disabled={!settingsForm.renewalReminder}
              />
              <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', ml: 1 }}>days before</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Expiry Alert</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>Notify users when subscription has expired.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.expiryAlert} onChange={(e) => setSettingsForm({ ...settingsForm, expiryAlert: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.expiryAlert ? ACCENT : '#9CA3AF' }}>{settingsForm.expiryAlert ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Webhook */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Webhook</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Send subscription events to your backend in real-time.</Typography>
        </Box>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>Enable Webhooks</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>POST subscription events to your endpoint.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch checked={settingsForm.enableWebhooks} onChange={(e) => setSettingsForm({ ...settingsForm, enableWebhooks: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
              <Typography sx={{ fontSize: '0.85rem', color: settingsForm.enableWebhooks ? ACCENT : '#9CA3AF' }}>{settingsForm.enableWebhooks ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 10 }}>
        <Button variant="contained" onClick={handleUpdateSettings} disabled={settingsSubmitting} sx={{ bgcolor: ACCENT, py: 1.5, px: 3, borderRadius: '12px', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: ACCENT_HOVER }, boxShadow: '0px 8px 16px rgba(109, 83, 244, 0.24)' }}>
          {settingsSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );

  
  // ─── Analytics Tab ───
  const renderAnalyticsTab = () => {
    if (analyticsLoading || !analyticsData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
          <CircularProgress />
        </Box>
      );
    }

    const { summary, revenueTrend, planDistribution, subscriberGrowth, churnRateTrend, statewiseSubscribers } = analyticsData;

    return (
      <Box sx={{ pb: 8 }}>
        {/* Top Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { title: 'Monthly Revenue', value: `₹${summary.monthlyRevenue.toLocaleString()}`, growth: summary.monthlyRevenueGrowth, icon: '₹', color: '#6D53F4', bgcolor: '#F0EDFE' },
            { title: 'Total Subscribers', value: summary.totalSubscribers.toLocaleString(), growth: summary.totalSubscribersGrowth, icon: '👥', color: '#10B981', bgcolor: '#D1FAE5' },
            { title: 'Churn Rate', value: `${summary.churnRate}%`, growth: summary.churnRateGrowth, icon: '📉', color: '#EF4444', bgcolor: '#FEE2E2', invertGrowth: true },
            { title: 'Avg. Revenue/User', value: `₹${summary.avgRevenuePerUser}`, growth: summary.avgRevenuePerUserGrowth, icon: '💳', color: '#3B82F6', bgcolor: '#DBEAFE' }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '8px', bgcolor: item.bgcolor, color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', mr: 2 }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 500 }}>{item.title}</Typography>
                    <Typography sx={{ color: '#111827', fontSize: '1.5rem', fontWeight: 700 }}>{item.value}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Typography sx={{ color: (item.invertGrowth ? item.growth < 0 : item.growth > 0) ? '#10B981' : '#EF4444', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    {item.growth > 0 ? '↗' : '↘'} {Math.abs(item.growth)}%
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem', ml: 1 }}>vs last month</Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Revenue Trend Area Chart */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px', height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Revenue Trend</Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Monthly revenue by plan (₹)</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6D53F4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6D53F4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} tickFormatter={(val) => `₹${val/1000}k`} />
                  <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#6B7280' }} />
                  <Area type="monotone" dataKey="value" name="Plus" stroke="#6D53F4" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                  <Area type="monotone" dataKey="max" name="Pro" stroke="#A78BFA" strokeWidth={2} fill="none" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Plan Distribution Donut Chart */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px', height: 400, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Plan Distribution</Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>Share of subscribers by plan</Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={planDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value" stroke="none">
                      {planDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ mt: 2 }}>
                {planDistribution.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.fill }} />
                      <Typography sx={{ fontSize: '0.85rem', color: '#4B5563', fontWeight: 500 }}>{item.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 600 }}>{item.value.toLocaleString()}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', width: 35, textAlign: 'right' }}>{item.percentage}%</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Subscriber Growth Bar Chart */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px', height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Subscriber Growth</Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Monthly active subscribers per plan</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={subscriberGrowth} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                  <RechartsTooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#6B7280' }} />
                  <Bar dataKey="Free" stackId="a" fill="#F3E8FF" radius={[0, 0, 4, 4]} barSize={20} />
                  <Bar dataKey="Plus" stackId="a" fill="#A78BFA" />
                  <Bar dataKey="Pro" stackId="a" fill="#6D53F4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Churn Rate Line Chart */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px', height: 400 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Churn Rate</Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>Monthly churn percentage across all plans</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={churnRateTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorChurn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} tickFormatter={(val) => `${val}%`} />
                  <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="rate" stroke="none" fill="url(#colorChurn)" />
                  <Line type="monotone" dataKey="rate" stroke="#EF4444" strokeWidth={3} dot={{ fill: '#EF4444', r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Statewise Subscribers */}
        <Box sx={{ maxWidth: 450 }}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ color: '#6D53F4', fontSize: 20 }} /> Statewise Subscribers
              </Typography>
              <Chip label="Top 8" size="small" sx={{ bgcolor: '#F0EDFE', color: '#6D53F4', fontWeight: 600, fontSize: '0.75rem' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {statewiseSubscribers.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ width: 130, fontSize: '0.85rem', color: '#4B5563', fontWeight: 500 }}>{item.name}</Typography>
                  <Box sx={{ flexGrow: 1, mx: 2 }}>
                    <LinearProgress variant="determinate" value={(item.count / item.max) * 100} sx={{ height: 6, borderRadius: 3, bgcolor: '#F3F4F6', '& .MuiLinearProgress-bar': { bgcolor: '#6D53F4', borderRadius: 3 } }} />
                  </Box>
                  <Typography sx={{ width: 40, textAlign: 'right', fontSize: '0.85rem', color: '#111827', fontWeight: 600 }}>{item.count}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  // ─── Render placeholder for other tabs ───
  const renderPlaceholderTab = (tabName) => (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 6, textAlign: 'center' }}>
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: ACCENT_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <Typography sx={{ fontSize: '2rem' }}>{tabName === 'Analytics' ? '📊' : '⚙️'}</Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>{tabName}</Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 400, mx: 'auto' }}>
        {tabName === 'Analytics' && 'View detailed analytics on subscription performance, revenue, and user engagement.'}
        {tabName === 'Settings' && 'Configure subscription settings, payment gateways, and notification preferences.'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 2, fontStyle: 'italic', fontSize: '0.78rem' }}>Coming soon</Typography>
    </Paper>
  );

  // ─── Feature Plan Limit Card (used in create/edit dialogs) ───
  const renderPlanLimitCard = (plan, color) => {
    const planId = plan._id;
    const selectVal = featureForm[`plan_${planId}`] || 'Not Available';
    const typeVal = featureForm[`planType_${planId}`] || 'boolean';
    const limitVal = featureForm[`planValue_${planId}`] || '';

    const handleSelectChange = (val) => {
      if (val === 'Limit') {
        setFeatureForm(prev => ({ ...prev, [`plan_${planId}`]: 'Limit', [`planType_${planId}`]: 'limit' }));
      } else if (val === 'Available') {
        setFeatureForm(prev => ({ ...prev, [`plan_${planId}`]: 'Available', [`planType_${planId}`]: 'boolean' }));
      } else {
        setFeatureForm(prev => ({ ...prev, [`plan_${planId}`]: 'Not Available', [`planType_${planId}`]: 'boolean' }));
      }
    };

    return (
      <Box key={planId} sx={{ flex: 1, p: 2, border: '1px solid #F3F4F6', borderRadius: '12px', bgcolor: '#FAFAFA' }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ color, fontWeight: 700 }}>{plan.name[0]}</span> {plan.name} Plan
        </Typography>
        <FormControl fullWidth size="small">
          <Select value={selectVal} onChange={(e) => handleSelectChange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#fff' }}>
            <MenuItem value="Not Available">Not Available</MenuItem>
            <MenuItem value="Available">Available</MenuItem>
            <MenuItem value="Limit">Limit</MenuItem>
          </Select>
        </FormControl>
        {selectVal === 'Limit' && (
          <TextField
            fullWidth
            placeholder="e.g. 5/day, Unlimited"
            size="small"
            value={limitVal}
            onChange={(e) => setFeatureForm(prev => ({ ...prev, [`planValue_${planId}`]: e.target.value }))}
            sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff' } }}
          />
        )}
      </Box>
    );
  };

  // ─── Coupon Form Fields (shared between create/edit) ───
  const renderCouponFormFields = () => (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Coupon Code <span style={{ color: '#DC2626' }}>*</span></Typography>
          <TextField fullWidth placeholder="e.g. WELCOME30" size="small" value={couponForm.code} onChange={(e) => setCouponForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Discount Type <span style={{ color: '#DC2626' }}>*</span></Typography>
          <FormControl fullWidth size="small">
            <Select value={couponForm.discountType} onChange={(e) => setCouponForm(prev => ({ ...prev, discountType: e.target.value }))} sx={{ borderRadius: '8px' }}>
              <MenuItem value="Percentage">Percentage (%)</MenuItem>
              <MenuItem value="Flat">Flat Amount (₹)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Discount Value <span style={{ color: '#DC2626' }}>*</span></Typography>
          <TextField fullWidth placeholder="e.g. 30" size="small" type="number" value={couponForm.discountValue} onChange={(e) => setCouponForm(prev => ({ ...prev, discountValue: Number(e.target.value) }))} InputProps={{ startAdornment: <InputAdornment position="start">{couponForm.discountType === 'Flat' ? '₹' : '%'}</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Applicable Plan</Typography>
          <FormControl fullWidth size="small">
            <Select value={couponForm.applicablePlan} onChange={(e) => setCouponForm(prev => ({ ...prev, applicablePlan: e.target.value }))} sx={{ borderRadius: '8px' }}>
              <MenuItem value="All">All</MenuItem>
              {plans.map(p => <MenuItem key={p._id} value={p.name}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Description</Typography>
        <TextField fullWidth multiline rows={2} placeholder="e.g. New user welcome discount" size="small" value={couponForm.description} onChange={(e) => setCouponForm(prev => ({ ...prev, description: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
      </Box>

      <Box sx={{ mb: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Geographic Scope</Typography>
        <FormControl fullWidth size="small">
          <Select value={couponForm.scopeType} onChange={(e) => setCouponForm(prev => ({ ...prev, scopeType: e.target.value }))} sx={{ borderRadius: '8px' }}>
            <MenuItem value="All India">All India (nationwide)</MenuItem>
            <MenuItem value="State-wise">State-wise (select states)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {couponForm.scopeType === 'State-wise' && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Target States <span style={{ color: '#DC2626' }}>*</span></Typography>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={couponForm.states}
              onChange={(e) => setCouponForm(prev => ({ ...prev, states: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (<Chip key={value} label={value} size="small" sx={{ bgcolor: ACCENT_LIGHT, color: ACCENT, borderRadius: '4px', fontWeight: 600, fontSize: '0.7rem' }} />))}
                </Box>
              )}
              sx={{ borderRadius: '8px' }}
            >
              {INDIAN_STATES.map((state) => (<MenuItem key={state} value={state}>{state}</MenuItem>))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Usage Limit <span style={{ color: '#DC2626' }}>*</span></Typography>
          <TextField fullWidth placeholder="100" size="small" type="number" value={couponForm.usageLimit} onChange={(e) => setCouponForm(prev => ({ ...prev, usageLimit: Number(e.target.value) }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Expiry Date <span style={{ color: '#DC2626' }}>*</span></Typography>
          <TextField fullWidth type="date" size="small" value={couponForm.expiryDate} onChange={(e) => setCouponForm(prev => ({ ...prev, expiryDate: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} InputLabelProps={{ shrink: true }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, borderTop: '1px solid #E5E7EB', mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>New users only</Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Only users who have never had a paid plan can use this.</Typography>
        </Box>
        <Switch checked={couponForm.newUsersOnly} onChange={(e) => setCouponForm(prev => ({ ...prev, newUsersOnly: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
      </Box>
    </>
  );

  const planColors = [ACCENT, '#16A34A', '#EA580C', '#0284C7', '#D97706'];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.5rem' }}>Subscription Management</Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>Manage subscription plans, features and pricing.</Typography>
        </Box>
        {!['Settings', 'Analytics'].includes(activeTab) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (activeTab === 'Coupons') {
                setCouponForm({ code: '', description: '', discountValue: '', discountType: 'Percentage', applicablePlan: 'All', scopeType: 'All India', states: [], usagePerUser: 1, newUsersOnly: false, usageLimit: '', expiryDate: '', status: 'Active' });
                setIsCreateCouponOpen(true);
              } else if (activeTab === 'Features') {
                setFeatureForm({ name: '', category: 'Discovery', subtitle: '', status: true });
                setIsAddFeatureOpen(true);
              } else {
                setPlanForm({ name: '', description: '', amount: '', period: 'monthly', interval: 1, trialPeriod: 0, enableTrialPeriod: false, isActive: true, displayOrder: 0, subscriberLimit: '' });
                setIsCreatePlanOpen(true);
              }
            }}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3, py: 1.2, fontSize: '0.9rem', bgcolor: ACCENT, color: '#fff', boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`, '&:hover': { bgcolor: ACCENT_HOVER, boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)` } }}
          >
            {activeTab === 'Coupons' ? 'Create Coupon' : activeTab === 'Features' ? 'Add Feature' : 'Create Plan'}
          </Button>
        )}
      </Box>

      {/* Tabs + Period Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 0 }}>
          {SUB_TABS.map((tab) => (
            <Button key={tab} onClick={() => setActiveTab(tab)} sx={{ textTransform: 'none', fontWeight: activeTab === tab ? 700 : 500, fontSize: '0.9rem', px: 2.5, py: 1.5, borderRadius: 0, color: activeTab === tab ? ACCENT : '#6B7280', borderBottom: activeTab === tab ? `2.5px solid ${ACCENT}` : '2.5px solid transparent', '&:hover': { bgcolor: 'transparent', color: ACCENT }, transition: 'all 0.2s ease' }}>
              {tab}
            </Button>
          ))}
        </Box>
        {activeTab === 'Plans' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            {PERIOD_OPTIONS.map((period) => (
              <Button key={period} onClick={() => setActivePeriod(period)} size="small" disableElevation sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', px: 2.5, py: 0.8, borderRadius: '8px', minWidth: 'auto', color: activePeriod === period ? '#fff' : '#6B7280', bgcolor: activePeriod === period ? ACCENT : 'transparent', '&:hover': { bgcolor: activePeriod === period ? ACCENT_HOVER : '#F3F4F6' }, transition: 'all 0.2s ease' }}>
                {period}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Tab Content */}
      {activeTab === 'Plans' && renderPlansTab()}
      {activeTab === 'Features' && renderFeaturesTab()}
      {activeTab === 'Coupons' && renderCouponsTab()}
      {activeTab === 'Settings' && renderSettingsTab()}
      {activeTab === 'Analytics' && renderAnalyticsTab()}
      {!['Plans', 'Features', 'Coupons', 'Settings', 'Analytics'].includes(activeTab) && renderPlaceholderTab(activeTab)}

      {/* ═══ MODALS ═══ */}

      {/* Plan Actions Menu */}
      <Menu
        anchorEl={planMenuAnchorEl}
        open={Boolean(planMenuAnchorEl)}
        onClose={() => setPlanMenuAnchorEl(null)}
        PaperProps={{ elevation: 0, sx: { mt: 1, border: '1px solid #E5E7EB', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', minWidth: 190, overflow: 'visible', '& .MuiMenuItem-root': { px: 2, py: 1.2, fontSize: '0.85rem', fontWeight: 500, color: '#374151', gap: 1.5, borderRadius: '6px', mx: 0.5, '&:hover': { bgcolor: '#F9FAFB' } } } }}
      >
        <MenuItem onClick={() => { openEditPlan(selectedPlanForMenu); setPlanMenuAnchorEl(null); }}>
          <EditIcon sx={{ fontSize: 18, color: '#6B7280' }} /> Edit Plan
        </MenuItem>
        <MenuItem onClick={() => { handleDuplicatePlan(selectedPlanForMenu); }}>
          <CopyIcon sx={{ fontSize: 18, color: '#6B7280' }} /> Duplicate Plan
        </MenuItem>
        <MenuItem onClick={() => { handleDeactivatePlan(selectedPlanForMenu); }} sx={{ color: '#D97706 !important' }}>
          <BlockIcon sx={{ fontSize: 18, color: '#D97706' }} /> {selectedPlanForMenu?.isActive ? 'Deactivate' : 'Activate'} Plan
        </MenuItem>
        <MenuItem onClick={() => { handleDeletePlan(selectedPlanForMenu?._id); }} sx={{ color: '#DC2626 !important' }}>
          <DeleteIcon sx={{ fontSize: 18, color: '#DC2626' }} /> Delete Plan
        </MenuItem>
      </Menu>

      {/* Create Plan Modal */}
      <Dialog open={isCreatePlanOpen} onClose={() => setIsCreatePlanOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Create New Plan</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>Set up a new subscription plan for your users.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsCreatePlanOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          {/* Live Preview Card */}
          <Box sx={{ mb: 3, p: 2.5, bgcolor: '#F3F1FE', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, fontSize: '1.2rem', fontWeight: 700 }}>?</Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>{planForm.name || 'Plan Name'}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>₹{planForm.amount || '0'} · Preview</Typography>
            </Box>
          </Box>

          {/* Plan Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Plan Name <span style={{ color: '#DC2626' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Pro, Plus, Starter" size="small" value={planForm.name} onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>This will be shown to users on the pricing page.</Typography>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Description</Typography>
            <TextField fullWidth multiline rows={2} placeholder="e.g. Best for power users who want unlimited access." size="small" value={planForm.description} onChange={(e) => setPlanForm(prev => ({ ...prev, description: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>Short tagline shown below the plan name.</Typography>
          </Box>

          {/* Price + Duration */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Price (₹) <span style={{ color: '#DC2626' }}>*</span></Typography>
              <TextField fullWidth placeholder="0" size="small" type="number" value={planForm.amount} onChange={(e) => setPlanForm(prev => ({ ...prev, amount: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Duration</Typography>
              <FormControl fullWidth size="small">
                <Select value={planForm.period} onChange={(e) => setPlanForm(prev => ({ ...prev, period: e.target.value }))} sx={{ borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } }}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Enable Trial Period */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, borderTop: '1px solid #F3F4F6' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Enable Trial Period</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Offer a free trial before billing starts.</Typography>
            </Box>
            <Switch checked={planForm.enableTrialPeriod} onChange={(e) => setPlanForm(prev => ({ ...prev, enableTrialPeriod: e.target.checked, trialPeriod: e.target.checked ? (prev.trialPeriod || 7) : 0 }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
          {planForm.enableTrialPeriod && (
            <Box sx={{ mb: 2.5 }}>
              <TextField fullWidth placeholder="7" size="small" type="number" value={planForm.trialPeriod} onChange={(e) => setPlanForm(prev => ({ ...prev, trialPeriod: Number(e.target.value) }))} InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            </Box>
          )}

          {/* Subscriber Limit */}
          <Box sx={{ mb: 2.5, pt: 2, borderTop: '1px solid #F3F4F6' }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Subscriber Limit</Typography>
            <TextField fullWidth placeholder="Unlimited" size="small" type="number" value={planForm.subscriberLimit} onChange={(e) => setPlanForm(prev => ({ ...prev, subscriberLimit: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>Leave blank for unlimited subscribers.</Typography>
          </Box>

          {/* Plan Active */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, borderTop: '1px solid #F3F4F6' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Plan Active</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Inactive plans are hidden from the pricing page.</Typography>
            </Box>
            <Switch checked={planForm.isActive} onChange={(e) => setPlanForm(prev => ({ ...prev, isActive: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsCreatePlanOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2.5 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePlan} disabled={planSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {planSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Modal */}
      <Dialog open={isEditPlanOpen} onClose={() => setIsEditPlanOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Edit Plan</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>Edit the subscription plan details.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsEditPlanOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          {/* Live Preview Card */}
          <Box sx={{ mb: 3, p: 2.5, bgcolor: '#F3F1FE', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ACCENT, fontSize: '1.2rem', fontWeight: 700 }}>{(planForm.name || '?')[0]?.toUpperCase()}</Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem' }}>{planForm.name || 'Plan Name'}</Typography>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>₹{planForm.amount || '0'} · Preview</Typography>
            </Box>
          </Box>

          {/* Plan Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Plan Name <span style={{ color: '#DC2626' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Pro, Plus, Starter" size="small" value={planForm.name} onChange={(e) => setPlanForm(prev => ({ ...prev, name: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>This will be shown to users on the pricing page.</Typography>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Description</Typography>
            <TextField fullWidth multiline rows={2} placeholder="e.g. Best for power users who want unlimited access." size="small" value={planForm.description} onChange={(e) => setPlanForm(prev => ({ ...prev, description: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>Short tagline shown below the plan name.</Typography>
          </Box>

          {/* Price + Duration */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Price (₹) <span style={{ color: '#DC2626' }}>*</span></Typography>
              <TextField fullWidth placeholder="0" size="small" type="number" value={planForm.amount} onChange={(e) => setPlanForm(prev => ({ ...prev, amount: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Duration</Typography>
              <FormControl fullWidth size="small">
                <Select value={planForm.period} onChange={(e) => setPlanForm(prev => ({ ...prev, period: e.target.value }))} sx={{ borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } }}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Enable Trial Period */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, borderTop: '1px solid #F3F4F6' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Enable Trial Period</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Offer a free trial before billing starts.</Typography>
            </Box>
            <Switch checked={planForm.enableTrialPeriod} onChange={(e) => setPlanForm(prev => ({ ...prev, enableTrialPeriod: e.target.checked, trialPeriod: e.target.checked ? (prev.trialPeriod || 7) : 0 }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
          {planForm.enableTrialPeriod && (
            <Box sx={{ mb: 2.5 }}>
              <TextField fullWidth placeholder="7" size="small" type="number" value={planForm.trialPeriod} onChange={(e) => setPlanForm(prev => ({ ...prev, trialPeriod: Number(e.target.value) }))} InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            </Box>
          )}

          {/* Subscriber Limit */}
          <Box sx={{ mb: 2.5, pt: 2, borderTop: '1px solid #F3F4F6' }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 0.8 }}>Subscriber Limit</Typography>
            <TextField fullWidth placeholder="Unlimited" size="small" type="number" value={planForm.subscriberLimit} onChange={(e) => setPlanForm(prev => ({ ...prev, subscriberLimit: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F9FAFB', '& fieldset': { borderColor: '#E5E7EB' } } }} />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem', mt: 0.5 }}>Leave blank for unlimited subscribers.</Typography>
          </Box>

          {/* Plan Active */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, borderTop: '1px solid #F3F4F6' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Plan Active</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Inactive plans are hidden from the pricing page.</Typography>
            </Box>
            <Switch checked={planForm.isActive} onChange={(e) => setPlanForm(prev => ({ ...prev, isActive: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsEditPlanOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2.5 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdatePlan} disabled={planSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {planSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Feature Modal */}
      <Dialog open={isAddFeatureOpen} onClose={() => setIsAddFeatureOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Add Feature</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Define a new feature and set limits per plan.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsAddFeatureOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Feature Name <span style={{ color: '#DC2626' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Profile Views, Super Likes" size="small" value={featureForm.name} onChange={(e) => setFeatureForm(prev => ({ ...prev, name: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Category</Typography>
            <FormControl fullWidth size="small">
              <Select value={featureForm.category} onChange={(e) => setFeatureForm(prev => ({ ...prev, category: e.target.value }))} sx={{ borderRadius: '8px' }}>
                {['Discovery', 'Messaging', 'Engagement', 'Privacy', 'Trust', 'Events', 'Analytics'].map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
            <Typography sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.85rem', mb: 2 }}>Feature availability per plan</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {plans.filter(p => p.isActive).map((plan, idx) => renderPlanLimitCard(plan, planColors[idx % planColors.length]))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, borderTop: '1px solid #E5E7EB' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Feature Active</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Inactive features are hidden from the comparison table.</Typography>
            </Box>
            <Switch checked={featureForm.status} onChange={(e) => setFeatureForm(prev => ({ ...prev, status: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsAddFeatureOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFeature} disabled={featureSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {featureSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Add Feature'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Feature Modal */}
      <Dialog open={isEditFeatureOpen} onClose={() => setIsEditFeatureOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Edit Feature</Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>Editing "{selectedFeatureForEdit?.name}"</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsEditFeatureOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Feature Name <span style={{ color: '#DC2626' }}>*</span></Typography>
            <TextField fullWidth value={featureForm.name} onChange={(e) => setFeatureForm(prev => ({ ...prev, name: e.target.value }))} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }} />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem', mb: 1 }}>Category</Typography>
            <FormControl fullWidth size="small">
              <Select value={featureForm.category} onChange={(e) => setFeatureForm(prev => ({ ...prev, category: e.target.value }))} sx={{ borderRadius: '8px' }}>
                {['Discovery', 'Messaging', 'Engagement', 'Privacy', 'Trust', 'Events', 'Analytics'].map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 3, pt: 3, borderTop: '1px solid #E5E7EB' }}>
            <Typography sx={{ fontWeight: 600, color: '#6B7280', fontSize: '0.85rem', mb: 2 }}>Feature availability per plan</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {plans.filter(p => p.isActive).map((plan, idx) => renderPlanLimitCard(plan, planColors[idx % planColors.length]))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, borderTop: '1px solid #E5E7EB' }}>
            <Box>
              <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.85rem' }}>Feature Active</Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>Inactive features are hidden from the comparison table.</Typography>
            </Box>
            <Switch checked={featureForm.status} onChange={(e) => setFeatureForm(prev => ({ ...prev, status: e.target.checked }))} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsEditFeatureOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateFeature} disabled={featureSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {featureSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Coupon Modal */}
      <Dialog open={isCreateCouponOpen} onClose={() => setIsCreateCouponOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Create Coupon</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>Set up a discount coupon for subscribers.</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsCreateCouponOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {renderCouponFormFields()}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsCreateCouponOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCoupon} disabled={couponSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {couponSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Coupon'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Coupon Modal */}
      <Dialog open={isEditCouponOpen} onClose={() => setIsEditCouponOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Edit Coupon</Typography>
            <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>Editing "{selectedCouponForEdit?.code}"</Typography>
          </Box>
          <IconButton size="small" onClick={() => setIsEditCouponOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {renderCouponFormFields()}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setIsEditCouponOpen(false)} sx={{ color: '#374151', textTransform: 'none', fontWeight: 600, border: '1px solid #E5E7EB', borderRadius: '8px', px: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateCoupon} disabled={couponSubmitting} sx={{ bgcolor: ACCENT, textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3, '&:hover': { bgcolor: ACCENT_HOVER } }}>
            {couponSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionManagement;
